// App Initializer - Handles initial data loading with progress tracking
// V2: LAZY LOADING - Only fetches metadata, cells loaded on-demand
import { getProductList, getProductMetadata } from '../modules/dataService.js'
import { getFilterMetadata } from '../modules/filterDataService.js'
import { markComplete, reportError } from './loadingService.js'
import { initFilterStore } from '../state/filterStore.js'
import { hasActiveSubscription, approvalStatus } from '../state/userState.js'

let isInitialized = false

/**
 * Check if user can access cell data (has active subscription)
 */
function canAccessData() {
  const hasSubscription = hasActiveSubscription()
  const approval = approvalStatus.value?.toUpperCase()
  const isApproved = approval === 'APPROVED'
  
  console.log('[AppInit] canAccessData:', { hasSubscription, approval, isApproved })
  return hasSubscription && isApproved
}

/**
 * Initialize app data with progress tracking (LAZY LOADING VERSION)
 * 
 * V2 Loading phases (MUCH FASTER!):
 * 1. CACHE_HYDRATION - Load from IndexedDB
 * 2. PRODUCT_LIST - Fetch product list (IDs only, ~1KB)
 * 3. FILTER_METADATA - Fetch filter options from /metadata endpoint (~10KB)
 * 
 * Cell data is now loaded ON-DEMAND when user:
 * - Opens a cell page
 * - Scrolls grid view (virtual scrolling)
 * - Opens scatter plot (KPIs loaded via /kpis endpoint)
 * 
 * This reduces initial load from ~100 API calls to just 2!
 * 
 * Note: If user has no subscription, skip data loading and complete immediately
 */
export async function initializeApp() {
  if (isInitialized) {
    console.log('[AppInit] Already initialized, skipping')
    markComplete()
    return
  }
  
  isInitialized = true
  console.log('[AppInit] 🚀 Starting app initialization (LAZY LOADING v2)...')
  
  try {
    // Step 0: restore saved state, THEN let the URL override it.
    //
    // `initFilterStore()` is localStorage + `loadFiltersFromURL()` in that order,
    // and until now it had NO CALLER anywhere in the app — this file called only
    // the localStorage half. The consequence was that a cold load applied nothing
    // from the query string: opening or reloading
    // `/explorer?chemistry=…&view=graph&x=…&y=…` restored whatever was last
    // persisted and silently dropped every param. Measured on staging
    // (2026-08-03): the URL above yielded zero active filters. Ion's handoff
    // buttons still worked because `applyNavLink` calls the URL reader itself, so
    // the gap only showed on shared links and on F5 — which is why it survived
    // this long.
    //
    // URL AFTER localStorage, not before: a link is a more specific intent than
    // "what this browser had open last time". That is also the order
    // `initFilterStore` documents for itself.
    initFilterStore()
    console.log('[AppInit] ✓ State loaded from localStorage + URL')
    
    // Check if user has data access - if not, complete early
    if (!canAccessData()) {
      console.log('[AppInit] ⚠️ User has no active subscription - skipping data loading')
      markComplete()
      return
    }
    
    // Step 1: Cache hydration happens automatically in dataService
    
    // Step 2: Load product list (just IDs and basic info - FAST!)
    const startTime = performance.now()
    const productList = await getProductList()
    const productsData = productList.products || []
    console.log(`[AppInit] ✓ Product list loaded: ${productsData.length} cells`)
    
    // Step 3: Load filter metadata from /metadata endpoint (SINGLE CALL!)
    // This replaces the old batchGetCells() which made ~100 individual API calls
    const metadata = await getFilterMetadata()
    console.log(`[AppInit] ✓ Filter metadata loaded: ${metadata.totalCells} cells, ${metadata.manufacturers?.length || 0} manufacturers`)
    
    // REMOVED: batchGetCells() - cells are now loaded on-demand!
    // Old code (SLOW - ~100 API calls):
    // const cellData = await batchGetCells(products, {}, 10)
    
    // REMOVED: getGlobalStats() - stats now come from /metadata endpoint
    // Old code:
    // await getGlobalStats()
    
    const duration = Math.round(performance.now() - startTime)
    console.log(`[AppInit] ✅ Initialization complete in ${duration}ms (LAZY LOADING)!`)
    console.log('[AppInit] 💡 Cell data will load on-demand when user navigates')
    
    // Mark loading as complete
    markComplete()
  } catch (err) {
    console.error('[AppInit] ❌ Initialization failed:', err)
    reportError('APP_INIT', err)
    
    // Still mark as complete so the app doesn't hang
    // User can still navigate, data will load on-demand
    markComplete()
  }
}

/**
 * Reset initialization state (for testing/debugging)
 */
export function resetInitialization() {
  isInitialized = false
}
